import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CATALOGO_DE_BLOCOS, type BlocoDaPaleta } from './catalogo-de-blocos';

/**
 * A prateleira de blocos, aberta sob demanda.
 *
 * Nasceu como barra fixa à esquerda e não se pagava: ficava ocupando um quinto
 * da tela o tempo todo para um gesto que acontece de vez em quando, e empurrava
 * o fluxo — que é o que a pessoa veio ver — para o canto.
 *
 * Agora abre pelo "+ Adicionar passo" do cabeçalho ou pelo "+" de uma ligação,
 * do mesmo lado dos outros painéis, e fecha depois de encaixar o bloco.
 */
export function PaletaDeBlocos({
  aoClicar,
  aoComecarArrasto,
  aoTerminarArrasto,
  aoFechar,
  destino,
}: {
  aoClicar: (bloco: BlocoDaPaleta) => void;
  aoComecarArrasto: (bloco: BlocoDaPaleta) => void;
  aoTerminarArrasto: () => void;
  aoFechar: () => void;
  /** Onde o clique vai encaixar o bloco — dito em voz alta para não surpreender. */
  destino: string;
}) {
  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="px-4 py-3 border-b flex items-start justify-between gap-2 shrink-0">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-foreground">Adicionar passo</h2>
          <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{destino}</p>
        </div>
        <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0 text-muted-foreground" onClick={aoFechar}>
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto px-2 py-2 space-y-3">
        {CATALOGO_DE_BLOCOS.map((grupo) => (
          <div key={grupo.nome}>
            <p className="px-1 pb-1 text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
              {grupo.nome}
            </p>
            <div className="space-y-0.5">
              {grupo.blocos.map((bloco) => {
                const Icone = bloco.icone;
                return (
                  <button
                    key={bloco.chave}
                    type="button"
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.effectAllowed = 'copy';
                      e.dataTransfer.setData('application/leadconversa-bloco', bloco.chave);
                      aoComecarArrasto(bloco);
                    }}
                    onDragEnd={aoTerminarArrasto}
                    onClick={() => aoClicar(bloco)}
                    title={bloco.descricao}
                    className="w-full flex items-start gap-2 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-muted/70 cursor-grab active:cursor-grabbing"
                  >
                    <Icone className={`w-4 h-4 mt-0.5 shrink-0 ${bloco.corDoIcone}`} strokeWidth={1.8} />
                    <span className="min-w-0">
                      <span className="block text-xs font-medium text-foreground">{bloco.rotulo}</span>
                      <span className="block text-[10px] leading-snug text-muted-foreground">
                        {bloco.descricao}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <p className="shrink-0 border-t px-3 py-2 text-[10px] leading-snug text-muted-foreground">
        Clique para encaixar em sequência, ou arraste para soltar onde quiser.
      </p>
    </div>
  );
}
